import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from '../components/loader';
import { ArrowLeft, Trophy } from 'lucide-react';
import { Tabs } from '../components/ui/tabs';
import { useLeaderboard, TimeRange } from '../hooks/use-leaderboard';
import { useWorkspace } from '../context/workspace-context';

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<TimeRange>('all_time');
  const { currentWorkspace } = useWorkspace();

  const {
    data: leaderboard,
    isLoading,
    error,
  } = useLeaderboard(timeRange, currentWorkspace?.id);

  const handleTabChange = (tab: string) => {
    setTimeRange(tab as TimeRange);
  };

  if (isLoading) return <Loader />;
  if (error)
    return (
      <div className="flex flex-col min-h-screen max-w-lg mx-auto bg-bg-main px-4 py-6">
        <h1 className="text-xl font-bold text-incorrect mb-4">Error</h1>
        <p className="text-title mb-2">
          Failed to load leaderboard. Please try again.
        </p>
        <p className="text-sm text-subheading">{(error as Error).message}</p>
        <button
          onClick={() => navigate('/courses')}
          className="mt-4 bg-accent-purple text-white px-4 py-2 rounded-lg"
        >
          Back to Courses
        </button>
      </div>
    );

  const entries = leaderboard?.entries || [];
  const currentUserEntry = leaderboard?.currentUserEntry;
  const currentUserRank = leaderboard?.currentUserRank;

  return (
    <div className="flex flex-col min-h-screen max-w-lg mx-auto bg-bg-main px-4 py-6">
      <header className="mb-6">
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate('/courses')}
            className="text-title hover:text-accent-purple transition-colors mr-4"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-xl font-bold text-title">Leaderboard</h1>
        </div>

        <Tabs
          activeTab={timeRange}
          onTabChange={handleTabChange}
          tabs={[
            { id: 'all_time', label: 'All Time' },
            { id: 'month_to_date', label: 'This Month' },
          ]}
          className="mb-6"
        />
      </header>

      <main className="flex-1 space-y-4">
        {entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Trophy className="h-16 w-16 text-gray-300 mb-4" />
            <h2 className="text-lg font-medium text-gray-700 mb-2">
              Leaderboard is empty
            </h2>
            <p className="text-gray-500 max-w-md">
              {timeRange === 'all_time'
                ? 'No points have been earned yet. Complete courses to climb the leaderboard!'
                : 'No points have been earned this month. Start learning to get on the board!'}
            </p>
          </div>
        ) : (
          <>
            {entries.slice(0, 10).map((entry) => (
              <div
                key={entry.userId}
                className={`flex items-center gap-4 p-4 rounded-3xl border transition-colors ${
                  entry.rank <= 3
                    ? 'bg-accent-pink hover:bg-accent-pink/90'
                    : 'bg-white hover:bg-gray-50'
                } ${entry.isCurrentUser ? 'border-accent-purple' : ''}`}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-accent-orange font-bold text-base w-6">
                    #{entry.rank}
                  </span>
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#f5ede4]">
                    <span className="text-title font-medium text-xs">
                      {entry.name?.[0]?.toUpperCase()}
                      {entry.surname?.[0]?.toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm text-title">
                      {entry.name} {entry.surname}
                      {entry.isCurrentUser && (
                        <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-accent-purple/20 text-title">
                          You
                        </span>
                      )}
                    </p>
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-accent-orange" />
                      <span className="font-medium text-sm text-title">
                        {entry.totalPoints} pts
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Current user's position if not in top 10 */}
        {currentUserEntry && currentUserRank && currentUserRank > 10 && (
          <>
            <div className="h-px bg-gray-200 my-6 relative">
              <span className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-bg-main px-4 text-sm text-subheading">
                Your Position
              </span>
            </div>
            <div
              key={`user-${currentUserEntry.userId}`}
              className="flex items-center gap-4 p-4 rounded-3xl border border-accent-purple bg-white"
            >
              <div className="flex items-center space-x-3">
                <span className="text-accent-orange font-bold text-base w-6">
                  #{currentUserRank}
                </span>
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#f5ede4]">
                  <span className="text-title font-medium text-xs">
                    {currentUserEntry.name?.[0]?.toUpperCase()}
                    {currentUserEntry.surname?.[0]?.toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm text-title">
                    {currentUserEntry.name} {currentUserEntry.surname}
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-accent-purple/20 text-title">
                      You
                    </span>
                  </p>
                  <div className="flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-accent-orange" />
                    <span className="font-medium text-sm text-title">
                      {currentUserEntry.totalPoints} pts
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default LeaderboardPage;
